import React, {
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Col,
	Flex,
	Form,
	Row,
	Typography,
} from 'antd';
import { useQuery, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUnlink } from '@fortawesome/pro-light-svg-icons';
import { debounce } from 'lodash';
import { SaveOutlined } from '@ant-design/icons';
import {
	FeatureFlags,
	FixtureTypes,
} from '@shared/utils/constants';
import isItemPortCall from '@shared/utils/isItemPortCall';
import { isPortCallCommencementDto } from '@shared/utils/portCallHelpers';
import type { UpdateVoyageAttributes } from '@api/features/voyages/updateVoyage';
import type { UpdatePortCallRequest } from '@api/features/ops/updatePortCall';
import Button from '@client/components/Button';
import Select from '@client/components/Select';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import {
	getRobsFromPortCall,
	getVoyages,
	updatePortCall,
	updateVoyage,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import { useMiscHandlers } from '@client/screens/estimates/details/context/hooks/useMiscHandlers';
import LinkedContractData from '@client/components/LinkedContractData';
import VoyageSelector from './VoyageSelector/VoyageSelector';
import { useVoyage } from './VoyageProvider/VoyageProvider';
import { useItinerary } from './ItineraryTab/ItineraryProvider';
import {
	CommencementRobBunker,
	getRelevantBunkerTables,
	ItineraryBunkerRecord,
	NullableItineraryBunkerRecord,
} from './ItineraryTab/utils/bunkerTabHelpers';
import {
	getPortTimezone,
	normalizePortDateTime,
	PortAwareFormDatePicker,
	TIME_ZONE_FORMAT_OPTIONS,
	toPortDateTimeInputValue,
} from './ItineraryTab/components/PortAwareDatePicker';
import styles from './styles/CommencementLinking.module.css';

const CommencementLinking = () => {
	const [searchQuery, setSearchQuery] = useState<string | undefined>();
	const [loading, setLoading] = useState(false);

	const queryClient = useQueryClient();
	const {
		voyageDetails,
		refreshVoyageDetails,
		refreshPnl,
	} = useVoyage();

	const {
		portOptions,
	} = useMiscHandlers();

	const usesTheShip = useFeatureFlag(FeatureFlags.THE_SHIP);

	const onSearch = useMemo(() => {
		return debounce((q) => setSearchQuery(q), 400);
	}, [setSearchQuery]);

	const { data: allVoyages } = useQuery(
		['voyages', searchQuery, voyageDetails.vesselId, voyageDetails.fixingCompanyId],
		() =>
			getVoyages({
				search: searchQuery,
				vesselId: voyageDetails.vesselId,
				disablePagination: true,
				includeCompleted: true,
				fixingCompanyId: voyageDetails.fixingCompanyId ?? undefined,
			}),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		},
	);

	const {
		itinerary,
		onSaveRob,
		onRemoveFuelQueueItem,
		refreshItinerary,
		onSaveFuelQueueItem,
		onDeleteRob,
		onAddNewRob,
		tcInCompletionBunkers,
		setItineraryShouldUpdate,
		timeZoneFormat,
	} = useItinerary();

	const [selectedPreviousContractId, setSelectedContractId] = useState<number | null | undefined>(
		voyageDetails.previousVoyageId,
	);

	const [form] = Form.useForm();

	const filteredVoyages = useMemo(() => {
		if (allVoyages == null) {
			return [];
		}

		return allVoyages.rows.filter((v) => (
			v.id !== voyageDetails.id &&
			v.id !== voyageDetails.nextVoyageId
		));
	}, [voyageDetails, allVoyages]);

	const portCalls = useMemo(() => {
		if (itinerary == null) {
			return [];
		}

		return itinerary.filter((entry) => isItemPortCall(entry)) as ItineraryPortCallDto[];
	}, [itinerary]);

	const commencementEntry = portCalls.find(isPortCallCommencementDto);

	const [commencementPort, setCommencementPort] = useState(
		commencementEntry?.port?.id ||
		voyageDetails.commencementPort?.id ||
		voyageDetails.estimatedCommencementPort?.id,
	);
	const commencementPortTimezone =
		commencementEntry?.port?.timezone ??
		voyageDetails.commencementPort?.timezone ??
		voyageDetails.estimatedCommencementPort?.timezone ??
		getPortTimezone(commencementPort);

	const {
		data: fetchedRobs,
		isFetching: fetchedRobsLoading,
		refetch: refreshFetchedRobs,
	} = useQuery(
		['fetchedRobs', commencementEntry?.id],
		() => getRobsFromPortCall(commencementEntry?.vesselId!, commencementEntry?.id!),
		{
			enabled: commencementEntry != null,
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const onSave = async () => {
		setLoading(true);

		const values = form.getFieldsValue();
		const selectedTimeZoneFormat = values.timeZoneFormat ?? timeZoneFormat;
		const selectedPortTimezone = getPortTimezone(values.commencementPort);
		const estimatedCommencementDate = normalizePortDateTime(
			values.estimatedCommencementDate,
			selectedTimeZoneFormat,
			selectedPortTimezone,
		);
		const actualCommencementDate = normalizePortDateTime(
			values.actualCommencementDate,
			selectedTimeZoneFormat,
			selectedPortTimezone,
		);
		const voyageAttributes: UpdateVoyageAttributes = {};
		const portCallAttributes: UpdatePortCallRequest['attributes'] = {};

		if (commencementEntry != null) {
			portCallAttributes.departureDate =
				actualCommencementDate ?? estimatedCommencementDate ?? undefined;
			portCallAttributes.port = values.commencementPort;

			voyageAttributes.commencementPort = values.commencementPort;
			voyageAttributes.commencementDate = actualCommencementDate;

			try {
				await updateVoyage(voyageDetails.id, voyageAttributes);
				await updatePortCall(voyageDetails.vesselId, commencementEntry.id, portCallAttributes);
				await refreshVoyageDetails();
				await refreshItinerary();
				await refreshPnl();
				queryClient.invalidateQueries(['robs', voyageDetails.id]);
				setItineraryShouldUpdate(true);
				showSuccessNotification('Commencement details updated successfully');
			} catch (e) {
				showErrorNotification('Failed to update commencement details', e as Error);
			} finally {
				setLoading(false);
			}
		}
	};

	const selectPreviousVoyage = async (attributes: UpdateVoyageAttributes) => {
		try {
			setSelectedContractId(
				attributes.previousVoyageId == null ? null : Number(attributes.previousVoyageId),
			);

			await updateVoyage(
				voyageDetails.id,
				attributes,
			);

			await refreshVoyageDetails();
			await refreshItinerary();
			refreshPnl();
			setItineraryShouldUpdate(true);

			showSuccessNotification('Sucessfully linked to previous contract');
		} catch (e) {
			showErrorNotification('Failed to update voyage', e as Error);
			setSelectedContractId(null);
		}
	};
	
	const createRob = async (
		id: number | null | undefined,
		newRow: ItineraryBunkerRecord,
	) => {
		await onAddNewRob(id, newRow, false);
		refreshFetchedRobs();
	};

	const saveRob = async (
		id: number | string | undefined,
		portCallActionId: number | null | undefined,
		rob: NullableItineraryBunkerRecord,
	) => {
		await onSaveRob(id, portCallActionId, rob);
		refreshFetchedRobs();
	};

	const deleteRob = async (
		id: number | string | undefined,
	) => {
		await onDeleteRob(id);
		refreshFetchedRobs();
	};

	const removeFuelQueueItem = async (
		row: CommencementRobBunker,
	) => {
		await onRemoveFuelQueueItem(row, fetchedRobs?.commencement);
		refreshFetchedRobs();
	};

	const saveFuelQueueItem = async (
		portCallActionId: number | string | undefined,
		robBunkerId: number,
		fuelQueue: CommencementRobBunker[],
	) => {
		await onSaveFuelQueueItem(portCallActionId, robBunkerId, fuelQueue);
		refreshFetchedRobs();
	};

	return (
		<Row gutter={[16, 16]}>
			<Col className={styles.divider} span={10}>
				<Flex vertical gap={10}>
					<Typography.Title level={2}>
						Option 1: Link this contract to its previous contract
					</Typography.Title>
					<Typography.Text>
						This ensures the contracts are back-to-back
					</Typography.Text>
					<Typography.Paragraph>
						This contract will commence at the completion of the previous contract.
						The completion date, port and bunkers will be used for the commencement of this contract
					</Typography.Paragraph>
					<b style={{ marginTop: 8 }}>Select Previous Contract</b>
					<VoyageSelector
						testId="commencement-linking-voyage-selector"
						value={selectedPreviousContractId}
						setSelectedContractId={setSelectedContractId}
						type="previous"
						allowClear
						onSearch={onSearch}
						voyages={filteredVoyages}
						startOfVesselOwnershipPeriod={
							null
						}
						defaultValue={voyageDetails.linkedTcInVoyageId === voyageDetails.previousVoyageId ?
							voyageDetails.linkedTcInVoyageId :
							voyageDetails.previousVoyageId}
						onSelect={(v) => {
							if (v === -1) {
								selectPreviousVoyage({ previousVoyageId: -1 });

								return;
							}

							const selectedVoyage = filteredVoyages.find((vo) => vo.id === v);

							if (v != null && selectedVoyage == null) {
								return;
							}

							let attributes = {};

							if (selectedVoyage?.fixtureType !== FixtureTypes.TC_IN) {
								attributes = {
									previousVoyageId: v ?? null,
								};
							} else {
								attributes = {
									linkedTcInVoyageId: v ?? null,
									previousVoyageId: v ?? null,
								};
							}

							selectPreviousVoyage(attributes);
						}}
					/>
					{voyageDetails.previousVoyage != null && (
						<Alert
							className={styles.marginTop}
							type="success"
							showIcon
							message={(
								<Flex justify="space-between" align="center">
									This voyage is linked to a previous voyage
									<Button
										icon={(<FontAwesomeIcon icon={faUnlink} />)}
										type="link"
										danger
										onClick={() => {
											setSelectedContractId(null);
											selectPreviousVoyage({ previousVoyageId: null });
										}}
									>
										Unlink
									</Button>
								</Flex>
							)}
						/>
					)}
					<LinkedContractData 
						selectedPreviousContractId={selectedPreviousContractId}
						voyages={allVoyages?.rows ?? []}
					/>
				</Flex>
			</Col>
			<Col span={14}>
				<Typography.Title level={2}>
					Option 2: This is the first contract for this vessel
				</Typography.Title>
				<Typography.Text>
					Manually enter the commencement port, bunkers and date
				</Typography.Text>
				<Typography.Paragraph>
					This option should be chosen if this is the very first contract
					for this vessel in ClearVoyage
				</Typography.Paragraph>
				<Flex gap={20} vertical justify="space-between" align='space-between'>
					<Form
						initialValues={{
							commencementPort,
							timeZoneFormat,
							estimatedCommencementDate: toPortDateTimeInputValue(
								voyageDetails?.estimatedCommencementDate,
								timeZoneFormat,
								commencementPortTimezone,
							),
							actualCommencementDate: toPortDateTimeInputValue(
								commencementEntry?.departureDate,
								timeZoneFormat,
								commencementPortTimezone,
							),
						}}
						onValuesChange={(changedValues) => {
							if (changedValues.commencementPort != null) {
								setCommencementPort(changedValues.commencementPort);
							}
						}}
						form={form}
						layout="vertical"
						disabled={voyageDetails.previousVoyageId != null}
						colon={false}
					>
						<Form.Item name="commencementPort" label="Commencement port">
							<Select
								showSearch
								placeholder="Select Commencement Port"
								options={portOptions}
							/>
						</Form.Item>
						<Form.Item name="timeZoneFormat" label="Time format">
							<Select
								options={TIME_ZONE_FORMAT_OPTIONS}
							/>
						</Form.Item>
						<Form.Item name="estimatedCommencementDate" label="Est. Departure date from commencement port">
							<PortAwareFormDatePicker
								formInstance={form}
								timezone={commencementPortTimezone}
								portFieldName="commencementPort"
								timeZoneFormatFieldName="timeZoneFormat"
								defaultTimeZoneFormat={timeZoneFormat}
								time
								allowClear={false}
							/>
						</Form.Item>
						<Form.Item name="actualCommencementDate" label="Act. Departure date from commencement port">
							<PortAwareFormDatePicker
								formInstance={form}
								timezone={commencementPortTimezone}
								portFieldName="commencementPort"
								timeZoneFormatFieldName="timeZoneFormat"
								defaultTimeZoneFormat={timeZoneFormat}
								time
								allowClear
							/>
						</Form.Item>
						{(commencementEntry?.departureDate != null && usesTheShip) && (
							<Alert 
								showIcon 
								type="error" 
								description="Updating this departure date will remove all manually created entries on your itinerary" 
								message="Warning" 
							/>
						)}
						{(commencementEntry != null && fetchedRobs != null) && (
							getRelevantBunkerTables({
								voyageDetails,
								robs: fetchedRobs,
								onAddNew: createRob,
								onSave: saveRob,
								onDelete: deleteRob,
								selectedEntry: commencementEntry,
								onRemoveFuelQueueItem: removeFuelQueueItem,
								onSaveFuelQueueItem: saveFuelQueueItem,
								allowAddNew: true,
								disabled: voyageDetails.previousVoyageId != null,
								tcInCompletionBunkers,
							})
						)}
					</Form>
					<Button
						icon={(<SaveOutlined />)}
						type="primary"
						onClick={onSave}
						disabled={voyageDetails.previousVoyageId != null}
						loading={fetchedRobsLoading || loading}
					>
						Save commencement details
					</Button>
				</Flex>
			</Col>
		</Row>
	);
};

export default CommencementLinking;
